import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Leaf, Award } from "lucide-react";
import ProductCard from "../components/ProductCard";
import CertificationsStrip from "../components/CertificationsStrip";
import { HERO_IMAGE, productsData } from "../lib/api";

import { Helmet } from "react-helmet-async";

const HomePage = () => {
  return (
    <main data-testid="home-page">
      <Helmet>
        <title>Gralon Global — Premium Botanical Exports from India</title>
        <meta name="description" content="Leading India-based exporter of premium psyllium husk, chia seeds, flax seeds, and Ayurvedic herbs. Pure origins, global reach, and lab-verified quality." />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Gralon Global",
              "url": "https://www.gralonglobal.com",
              "logo": "https://www.gralonglobal.com/favicon.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-77376-71164",
                "contactType": "export sales",
                "areaServed": "Worldwide",
                "availableLanguage": ["en", "hi"]
              },
              "sameAs": [
                "https://www.linkedin.com/company/gralonglobal"
              ]
            }
          `}
        </script>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "Gralon Global",
              "image": "https://www.gralonglobal.com/favicon.png",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "13/2 Vinayak Nagar, Bohra Ganesh Ji Road",
                "addressLocality": "Udaipur",
                "addressRegion": "Rajasthan",
                "postalCode": "313001",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 24.5854,
                "longitude": 73.7125
              },
              "url": "https://www.gralonglobal.com",
              "telephone": "+917737671164",
              "priceRange": "$$"
            }
          `}
        </script>
      </Helmet>
      {/* HERO */}
      <section className="relative min-h-[100vh] flex flex-col overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Premium Botanical Sourcing in India - Gralon Global"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="grain-overlay" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 flex-grow flex items-center py-20 lg:py-10">
          <div className="max-w-3xl text-cream animate-fade-up">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gold mb-8">
              Gralon Global · Est. India
            </div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight">
              Premium Botanical Exports,
              <br />
              <span className="italic text-gold-light">Direct from India.</span>
            </h1>
            <p className="mt-8 max-w-xl text-cream/85 text-lg leading-relaxed">
              Premium-grade psyllium husk, super seeds and Ayurvedic herbs —
              cultivated across the heartlands of India and exported with
              uncompromising quality to wellness brands worldwide.
            </p>
            <div className="mt-10 flex flex-col md:flex-row gap-4">
              <Link
                to="/products"
                data-testid="hero-explore-btn"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-8 py-4 bg-cream text-forest-dark text-[13px] sm:text-sm tracking-[0.1em] sm:tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
              >
                Explore Catalog
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-inquire-btn"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-8 py-4 border border-cream/50 text-cream text-[13px] sm:text-sm tracking-[0.1em] sm:tracking-[0.2em] uppercase hover:bg-cream hover:text-forest-dark transition-colors duration-300"
              >
                Request Quote
              </Link>
            </div>
          </div>
        </div>

        {/* Metric strip - Now integrated into flex flow to prevent overlap */}
        <div className="relative border-t border-cream/15 bg-black/40 backdrop-blur-xl z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6 text-cream">
            {[
              [productsData.length.toString(), "Premium Products"],
              ["20+", "Countries Served"],
              ["100%", "Lab-Tested Quality"],
              ["24mo", "Typical Shelf Life"],
            ].map(([n, l]) => (
              <div key={l} className="flex items-baseline gap-3">
                <span className="font-serif text-3xl text-gold-light">{n}</span>
                <span className="text-[11px] tracking-[0.2em] uppercase text-cream/70">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO / VALUE */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-eyebrow">Who We Are</div>
            <h2 className="font-serif font-light mt-6 text-4xl lg:text-6xl leading-[1.05] text-ink">
              Cultivating <span className="italic text-forest">trust</span>, exporting
              <span className="italic text-gold"> purity</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-8">
            <p className="text-ink/80 text-lg leading-relaxed">
              From the sun-drenched fields of Rajasthan and Gujarat to the herbal
              belts of Madhya Pradesh, Gralon partners with trusted
              farmers and certified processing units to deliver the finest
              botanical commodities to international markets — with full
              traceability, lab-verified specifications, and consistent supply.
            </p>

            <div className="grid sm:grid-cols-3 gap-8 mt-12">
              {[
                {
                  Icon: Leaf,
                  title: "Origin-First",
                  desc: "Direct sourcing from India's most renowned growing regions.",
                },
                {
                  Icon: Award,
                  title: "Lab-Verified",
                  desc: "Every lot tested for purity, microbial load and pesticide residues.",
                },
                {
                  Icon: Globe,
                  title: "Global Logistics",
                  desc: "FOB / CIF / DDP — flexible shipping across 20+ countries.",
                },
              ].map(({ Icon, title, desc }) => (
                <div key={title} className="border-t border-forest/30 pt-5">
                  <Icon strokeWidth={1.2} className="w-7 h-7 text-forest" />
                  <h3 className="font-serif text-xl mt-4 text-ink">{title}</h3>
                  <p className="text-sm text-muted mt-2 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CertificationsStrip />

      {/* PRODUCTS */}
      <section className="py-24 lg:py-32 bg-cream-alt">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <div className="label-eyebrow">The Catalog</div>
              <h2 className="font-serif font-light mt-6 text-4xl lg:text-6xl leading-[1.05] text-ink max-w-3xl">
                {productsData.length} premium products.<br />
                <span className="italic text-forest">One uncompromising standard.</span>
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="home-view-all-products"
              className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-forest-dark hover:text-gold transition-colors duration-300"
            >
              View all products <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
            {productsData.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL REACH */}
      <section className="py-24 lg:py-32 bg-forest-deep text-cream relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-5">
            <div className="label-eyebrow text-gold">Global Reach</div>
            <h2 className="font-serif font-light mt-6 text-4xl lg:text-6xl leading-[1.05]">
              From Indian soil <span className="italic text-gold-light">to your shore.</span>
            </h2>
            <p className="mt-6 text-cream/80 leading-relaxed">
              Trusted by importers, formulators and food brands across North
              America, Europe, the Middle East, Australia and Southeast Asia.
            </p>
            <Link
              to="/contact"
              data-testid="reach-cta"
              className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-gold text-forest-deep text-sm tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
            >
              Talk to our Export Team
              <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-cream/10">
              {[
                "United States", "Canada", "United Kingdom", "Germany", "Netherlands",
                "France", "U.A.E.", "Saudi Arabia", "Australia",
                "Japan", "Singapore", "South Korea",
              ].map((c) => (
                <div key={c} className="bg-forest-deep p-6">
                  <div className="font-serif text-xl text-cream">{c}</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-gold mt-2">
                    Active Market
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
